import React from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

interface EvictionsLineChartProps {
  evictionsByMonth: [string, number][];
  preventionsByMonth: [string, number][];
  onYearSelect?: (year: string | null) => void;
}

const colorScale = ["#8FA7C7", "#E2E8F0", "#9DBAD5", "#B7C8DB", "#98AFC7", "#C0D3E0"];

const EvictionsLineChart: React.FC<EvictionsLineChartProps> = ({
  evictionsByMonth,
  preventionsByMonth,
  onYearSelect,
}) => {
  const windowSize = 9;

  const [allYears, setAllYears] = React.useState<string[]>([]);
  const [startIndex, setStartIndex] = React.useState(0);
  const [yearSelected, setYearSelected] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<any>(null);

  const hasInitialized = React.useRef(false);

  // Process monthly data into yearly counts
  const processData = (data: [string, number][]) => {
    const grouped: Record<string, number> = {};
    data.forEach(([date, count]) => {
      const year = date.split("-")[0];
      grouped[year] = (grouped[year] || 0) + count;
    });
    return grouped;
  };

  const evictionsByYear = processData(evictionsByMonth);
  const preventionsByYear = processData(preventionsByMonth);

  // Effect 1: compute allYears safely and drop future years
  React.useEffect(() => {
    let yearsSet = new Set([
      ...Object.keys(evictionsByYear),
      ...Object.keys(preventionsByYear),
    ]);

    let years = Array.from(yearsSet).sort();

    // ✅ Fail-safe: remove future years
    const currentYear = new Date().getFullYear();
    years = years.filter((y) => Number(y) <= currentYear);

    setAllYears(years);
  }, [evictionsByMonth, preventionsByMonth]);

  // Effect 2: initialize the visible window only once
  React.useEffect(() => {
    if (allYears.length > 0 && !hasInitialized.current) {
      const lastIndex = Math.max(0, allYears.length - windowSize);
      setStartIndex(lastIndex);
      hasInitialized.current = true;
    }
  }, [allYears]);

  // Effect 3: update chart data when startIndex or data changes
  React.useEffect(() => {
    if (allYears.length === 0) return;

    const visibleYears = allYears.slice(startIndex, startIndex + windowSize);

    const evictionCounts = visibleYears.map((y) => evictionsByYear[y] || 0);
    const preventionCounts = visibleYears.map((y) => preventionsByYear[y] || 0);

    setChartData({
      labels: visibleYears,
      datasets: [
        {
          label: "Evictions",
          data: evictionCounts,
          backgroundColor: "rgba(139, 92, 246, 0.2)",
          borderColor: "#8b5cf6",
          fill: true,
          tension: 0.3,
          borderWidth: 1,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#8b5cf6",
          pointBorderWidth: 1,
        },
        {
          label: "Preventions",
          data: preventionCounts,
          backgroundColor: "rgba(34, 197, 94, 0.2)",
          borderColor: "#22c55e",
          fill: true,
          tension: 0.3,
          borderWidth: 1,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#22c55e",
          pointBorderWidth: 1,
        },
      ],
    });
  }, [startIndex, allYears, evictionsByYear, preventionsByYear]);

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: { tension: 0.3 },
      point: { radius: 3, hoverRadius: 5 },
    },
    plugins: {
      title: {
        display: true,
        text: "Yearly Evictions and Preventions Trends",
        font: { size: 12, weight: "bold" },
        color: "#333",
        padding: { top: 0, bottom: 10 },
      },
      legend: {
        position: "top",
        labels: { boxWidth: 12, boxHeight: 12, padding: 10, font: { size: 10 }, usePointStyle: true },
      },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        titleColor: "#333",
        bodyColor: "#333",
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = typeof context.raw === "number" ? context.raw.toLocaleString() : context.raw;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, color: "#666" } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(180, 180, 180, 0.1)" },
        ticks: {
          font: { size: 10 },
          color: "#666",
          callback: (value) => {
            if (typeof value === "number") {
              if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
              if (value >= 1_000) return (value / 1_000).toFixed(0) + "K";
              return value.toLocaleString();
            }
            return value;
          },
        },
      },
    },
    onClick: (evt, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const year = chartData?.labels[index];
        setYearSelected(year);
        onYearSelect?.(year);
      }
    },
  };

  return (
    <div className="p-2 bg-white rounded-md relative">
      {!chartData ? (
        <div className="flex items-center justify-center h-64 gap-2 text-sm text-gray-500">
          <svg className="w-5 h-5 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading chart data...
        </div>
      ) : (
        <div style={{ minHeight: "300px", width: "100%" }}>
          <Line data={chartData} options={options} />

          {/* Navigation buttons */}
          <div className="absolute inset-x-0 -bottom-4 flex justify-between px-2">
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(Math.max(0, startIndex - 1))}
              disabled={startIndex === 0}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-[#374151]" />
            </button>
            <button
              className="w-6 h-6 flex items-center justify-center rounded-md bg-[#E5E7EB] hover:bg-[#D1D5DB] disabled:opacity-50"
              onClick={() => setStartIndex(startIndex + 1)}
              disabled={startIndex >= allYears.length - windowSize}
            >
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-[#374151]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvictionsLineChart;
